/**
 * Collapse (TipTap / AI) HTML to a STRUCTURED plain-text preview: block
 * boundaries become newlines and list items get a "- " bullet, so a bulleted
 * or multi-paragraph message reads as separate lines instead of one run-on
 * string. For surfaces that sit inside a `<button>` (the launcher peek) where
 * real block HTML would be invalid markup — render the result with
 * `white-space: pre-line`.
 *
 * `stripHtml` (single-line, whitespace-collapsed) stays the right tool for a
 * tight one/two-line chip; this is for a taller preview where structure helps.
 * Not a sanitizer — display of untrusted HTML is the BE sanitiser's job.
 */

const ENTITY_MAP: Record<string, string> = {
  "&nbsp;": " ",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&amp;": "&", // decoded last (handled by ordering below)
};

function decodeEntities(s: string): string {
  return (
    s
      .replace(/&#(\d+);/g, (_, n) => {
        const code = Number(n);
        return Number.isFinite(code) ? String.fromCodePoint(code) : _;
      })
      // Decode &amp; LAST so "&amp;lt;" doesn't collapse to "<".
      .replace(/&nbsp;|&lt;|&gt;|&quot;|&#39;|&apos;/gi, (m) => ENTITY_MAP[m.toLowerCase()] ?? m)
      .replace(/&amp;/gi, "&")
  );
}

export function htmlToPreviewText(input: string | null | undefined): string {
  if (!input) return "";
  // No tags/entities → plain text already; just normalise trailing space.
  if (!/[<&]/.test(input)) return input.trim();

  const withBreaks = input
    // A list item starts a new bulleted line.
    .replace(/<li[^>]*>/gi, "\n- ")
    // Block ends break the line.
    .replace(/<\/(p|div|ul|ol|li|blockquote|h[1-6]|tr)>/gi, "\n")
    // Explicit line breaks.
    .replace(/<br\s*\/?>/gi, "\n")
    // Everything else: drop the tag, keep its text.
    .replace(/<[^>]*>/g, "");

  const decoded = decodeEntities(withBreaks);

  // Trim each line, collapse intra-line whitespace, drop blank lines (bullets
  // and block breaks already separate the content).
  return decoded
    .split("\n")
    .map((line) => line.replace(/[ \t ]+/g, " ").trim())
    .filter((line) => line !== "")
    .join("\n");
}
